import React, { useRef, useState } from 'react';
import { color, Text } from '@starrez/starryui9';
import { ICellRenderer } from '../types';
import styled from 'styled-components';
import { ColDef } from 'ag-grid-community';
import { usePopper } from 'react-popper';
import { Highlighted, Modal } from '@starrez/starryui';
import { useServerGridContext } from '../../ServerPagedGrid/ServerPageGridContext';

interface ILinkColumnProps extends ICellRenderer<string> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick?: (data: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hoverComponent?: (props: any) => JSX.Element;
}

export const LinkColumn = ({ hoverComponent: Hover, ...props }: ILinkColumnProps) => {
    const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);

    const timedEvent = useRef(0);
    const { getCurrentHighlight } = useServerGridContext();

    const handleClick = () => {
        if (props.onClick) {
            props.onClick(props.data);
        }
    };

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom',
        modifiers: [
            { name: 'arrow', options: { element: arrowElement, padding: 30 } },
            {
                name: 'offset',
                options: {
                    offset: [0, 0],
                },
            },
        ],
    });

    const handleOnMouseOut = () => {
        window.clearTimeout(timedEvent.current);
        setVisible(false);
    };

    const handleOnMouseOver = () => {
        timedEvent.current = window.setTimeout(() => {
            setVisible(true);
        }, 500);
    };

    const handleHideHover = () => {
        setVisible(false);
    };

    const linkText = (
        <Text variant="normal.regular">
            <Highlighted Text={props.value.toString()} Highlight={getCurrentHighlight()} />
        </Text>
    );
    const linkButton = props.onClick ? <StyledLinkButton onClick={handleClick}>{linkText}</StyledLinkButton> : linkText;
    // if i have a hover component then use it.
    return Hover ? (
        <>
            <div onMouseLeave={handleOnMouseOut} onMouseEnter={handleOnMouseOver} ref={setReferenceElement}>
                {linkButton}

                {visible && (
                    <Modal>
                        <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                            <Hover {...props.data} hideHover={handleHideHover} />
                            <div ref={setArrowElement} style={{ ...styles.arrow, top: styles.popper.top === '0' ? '0' : undefined }}>
                                <Arrow positionTop={styles.popper.top} />
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    ) : (
        linkButton
    );
};

export function useLinkColumn<TRowDate>(onClick?: (data: TRowDate) => void, hoverComponent?: (props: TRowDate) => JSX.Element): ColDef {
    return {
        cellRendererFramework: LinkColumn,
        cellRendererParams: {
            onClick,
            hoverComponent,
        },
    };
}

export const StyledLinkButton = styled.button`
    color: ${color.Blue.B60};
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    width: 100%;
    text-align: left;

    * {
        cursor: pointer;
    }

    &:hover {
        text-decoration: underline;
    }
`;

interface IArrow {
    positionTop: string | number | undefined;
}

const Arrow = styled.div<IArrow>`
    transform: translateY(-8px);
    transform-origin: center;

    &:before {
        content: '';
        height: 16px;
        width: 16px;
        border: 1px solid ${color.Grey.G30};
        transform: ${(props) => (props.positionTop === '0' ? 'rotate(135deg)' : 'rotate(-45deg)')};
        display: inline-block;
        background: white;
        border-right-color: transparent;
        border-top-color: transparent;
    }
`;
